import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import { baseAxios } from "../../apis";
import {
  buildOrderMarkers,
  calculatePrecision,
  fmt,
} from "../../utils/tradeChart";
import { useActivityChartQuery } from "../../hooks/useTradeMarkerChart";
import { normalizePair } from "../../utils";
import { useBot } from "../../context/botContext";

// ======================================================
// API FETCHERS
// ======================================================

const isIDRPair = (symbol) => {
  return symbol && symbol.toUpperCase().endsWith("IDR");
};

const mapTimeframeToIndodax = (timeframe) => {
  const map = {
    "15m": "15",
    "1h": "60",
    "4h": "240",
    "1d": "1D",
    "1w": "1W",
  };
  return map[timeframe.toLowerCase()] || "60";
};

const getTimeRange = (timeframe, limit) => {
  const tf = timeframe.toLowerCase();
  let minutes;

  if (tf === "15m") minutes = 15;
  else if (tf === "1h") minutes = 60;
  else if (tf === "4h") minutes = 240;
  else if (tf === "1d") minutes = 1440;
  else if (tf === "1w") minutes = 10080;
  else minutes = 60;

  const now = Math.floor(Date.now() / 1000);
  const from = now - limit * minutes * 60;

  return { from, to: now };
};

// ── Date/Time helpers — respects user's browser locale & timezone ──────────
const userLocale = navigator.language || "id-ID";
const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDateTime = (unixSeconds) => {
  const d = new Date(unixSeconds * 1000);
  const opts = { timeZone: userTimeZone };
  const local = new Date(d.toLocaleString("en-US", opts));
  const dd = String(local.getDate()).padStart(2, "0");
  const mo = String(local.getMonth() + 1).padStart(2, "0");
  const yyyy = local.getFullYear();
  const hh = String(local.getHours()).padStart(2, "0");
  const mm = String(local.getMinutes()).padStart(2, "0");
  return `${dd}/${mo}/${yyyy}, ${hh}:${mm}`; // DD/MM/YYYY, HH:mm
};

// const formatDateShort = (unixSeconds) => {
//   const d = new Date(unixSeconds * 1000);
//   const opts = { timeZone: userTimeZone };
//   const local = new Date(d.toLocaleString("en-US", opts));
//   const dd = String(local.getDate()).padStart(2, "0");
//   const mo = String(local.getMonth() + 1).padStart(2, "0");
//   const hh = String(local.getHours()).padStart(2, "0");
//   const mm = String(local.getMinutes()).padStart(2, "0");
//   return `${dd}/${mo} ${hh}:${mm}`; // DD/MM HH:mm — no seconds
// };

const formatDateShort = (unixSeconds) => {
  const d = new Date(unixSeconds * 1000);
  const opts = { timeZone: userTimeZone };
  const local = new Date(d.toLocaleString("en-US", opts));
  const dd = String(local.getDate()).padStart(2, "0");
  const mo = String(local.getMonth() + 1).padStart(2, "0");
  const yy = String(local.getFullYear()).slice(-2); // 2 digit tahun
  const hh = String(local.getHours()).padStart(2, "0");
  const mm = String(local.getMinutes()).padStart(2, "0");
  return `${dd}/${mo}/${yy} ${hh}:${mm}`; // DD/MM/YY HH:mm
};

const fetchIndodaxCandles = async (
  symbol,
  interval,
  endTime = null,
  limit = 100,
  exchangeId,
) => {
  try {
    const pair = symbol.toUpperCase().replace(/[_-]/g, "");
    const tf = mapTimeframeToIndodax(interval);

    let from, to;
    if (endTime) {
      to = Math.floor(endTime / 1000);
      const timeRange = getTimeRange(interval, limit);
      from = to - (timeRange.to - timeRange.from);
    } else {
      const timeRange = getTimeRange(interval, limit);
      from = timeRange.from;
      to = timeRange.to;
    }

    let queryParams = `symbol=${symbol}&interval=${interval}&limit=${limit}&currency=${
      normalizePair(symbol).split("/")[1]
    }`;
    if (endTime) queryParams += `&endTime=${endTime}`;

    const getPair = normalizePair(symbol)?.split("/");
    const target = getPair[0];
    const base = getPair[1];

    queryParams = `target=${target}&base=${base}&exchange=${exchangeId}&interval=${interval}&limit=${limit}`;
    const url = `/api/bot_detail/chart_klines?${queryParams}`;
    const { data } = await baseAxios.get(url);

    if (Array.isArray(data)) {
      return data
        .map((candle) => ({
          time: candle.Time,
          open: parseFloat(candle.Open),
          high: parseFloat(candle.High),
          low: parseFloat(candle.Low),
          close: parseFloat(candle.Close),
        }))
        .filter((c) => !isNaN(c.time) && !isNaN(c.open))
        .sort((a, b) => a.time - b.time);
    }

    if (data.t && data.o && data.h && data.l && data.c) {
      return data.t
        .map((time, i) => ({
          time: time,
          open: parseFloat(data.o[i]),
          high: parseFloat(data.h[i]),
          low: parseFloat(data.l[i]),
          close: parseFloat(data.c[i]),
        }))
        .filter((c) => !isNaN(c.time) && !isNaN(c.open))
        .sort((a, b) => a.time - b.time);
    }

    return [];
  } catch (error) {
    console.error("Indodax API error:", error);
    return [];
  }
};

const fetchCandles = async (
  symbol,
  interval,
  endTime = null,
  limit = 100,
  exchangeId,
  apiRegion = null,
) => {
  if (!symbol) return [];

  const pair = normalizePair(symbol)?.split("/");
  const target = pair[0];
  const base = pair[1];

  let queryParams = `target=${target}&base=${base}&exchange=${exchangeId}&interval=${interval}&limit=${limit}`;
  if (endTime) queryParams += `&endTime=${endTime}`;
  if (apiRegion) queryParams += `&region=${apiRegion}`;

  const BINANCE_URL = `https://api.binance.com/api/v3/klines?${queryParams}`;
  const BACKEND_URL = `/api/bot_detail/chart_klines?${queryParams}`;

  let rawData;
  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Binance timeout")), 3500),
    );

    const fetchPromise = fetch(BINANCE_URL).then((res) => {
      if (!res.ok) throw new Error("Binance failed");
      return res.json();
    });

    rawData = await Promise.race([fetchPromise, timeoutPromise]);
  } catch (error) {
    const { data } = await baseAxios.get(BACKEND_URL);
    rawData = data;
  }

  return rawData
    .map((d) => ({
      time: d[0] / 1000,
      open: parseFloat(d[1]),
      high: parseFloat(d[2]),
      low: parseFloat(d[3]),
      close: parseFloat(d[4]),
    }))
    .filter((c) => !isNaN(c.time) && !isNaN(c.open))
    .sort((a, b) => a.time - b.time);
};

// ======================================================
// XAXIS LABEL OVERLAY — rendered outside canvas so
// candlesticks can NEVER overlap the labels
// ======================================================

const XAXIS_HEIGHT = 28; // px — height of the white label strip

function XAxisOverlay({ chartRef, candles, zoomState }) {
  const [labels, setLabels] = useState([]);
  const rafRef = useRef(null);

  const computeLabels = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      const chart = chartRef.current?.getEchartsInstance();
      if (!chart || !candles.length) return;

      const opt = chart.getOption();
      const dz = opt?.dataZoom?.[0];
      if (!dz) return;

      const startPct = (dz.start ?? zoomState.start) / 100;
      const endPct = (dz.end ?? zoomState.end) / 100;
      const total = candles.length;
      const startIdx = Math.max(0, Math.floor(startPct * total));
      const endIdx = Math.min(total - 1, Math.ceil(endPct * total));
      const visible = Math.max(1, endIdx - startIdx);

      // Get pixel positions for first and last visible candle
      const pxStart = chart.convertToPixel({ gridIndex: 0 }, [startIdx, 0]);
      const pxEnd = chart.convertToPixel({ gridIndex: 0 }, [endIdx, 0]);
      if (!pxStart || !pxEnd) return;

      const totalPxWidth = Math.abs(pxEnd[0] - pxStart[0]);
      const minLabelPx = 72; // minimum px between labels to avoid overlap
      const maxLabels = Math.max(1, Math.floor(totalPxWidth / minLabelPx));
      const step = Math.ceil(visible / maxLabels);

      const result = [];
      let lastX = -Infinity;

      for (let i = startIdx; i <= endIdx; i++) {
        if ((i - startIdx) % step !== 0) continue;
        try {
          const px = chart.convertToPixel({ gridIndex: 0 }, [i, 0]);
          if (!px) continue;
          const x = px[0];

          // Extra guard: skip if too close to previous label
          if (x - lastX < minLabelPx) continue;
          lastX = x;

          result.push({ x, label: formatDateShort(candles[i].time) });
        } catch (_) {}
      }

      setLabels(result);
    });
  }, [chartRef, candles, zoomState]);

  // Recompute on zoom / candle change
  useEffect(() => {
    computeLabels();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [computeLabels]);

  // Recompute on window resize
  useEffect(() => {
    const onResize = () => computeLabels();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [computeLabels]);

  // Recompute after chart finishes rendering (catches initial load)
  useEffect(() => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;
    chart.on("finished", computeLabels);
    return () => chart.off("finished", computeLabels);
  }, [chartRef, computeLabels]);

  return (
    <div
      style={{
        position: "relative",
        height: XAXIS_HEIGHT,
        background: "#FFFFFF",
        borderTop: "1px solid #E0E3EB",
        overflow: "hidden",
      }}
    >
      {labels.map((l, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: l.x,
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: 10,
            color: "#787B86",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {l.label}
        </span>
      ))}
    </div>
  );
}

// ======================================================
// COMPONENT
// ======================================================

export default function TradeMarkersChart({ botId, height = 450, pairSymbol }) {
  const { botDetail, myExchanges } = useBot();
  const [selectedTimeframe, setSelectedTimeframe] = useState("1D");
  const [candles, setCandles] = useState([]);
  const [zoomState, setZoomState] = useState({ start: 50, end: 100 });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [livePrice, setLivePrice] = useState(null);
  const wsReadyRef = useRef(false);

  const wsRef = useRef(null);
  const chartRef = useRef(null);
  const loadingRef = useRef(false);
  const currentSymbolRef = useRef(pairSymbol);
  const currentTimeframeRef = useRef(selectedTimeframe);
  const dataZoomTimeoutRef = useRef(null);

  const { data: activityData } = useActivityChartQuery(
    botId,
    selectedTimeframe,
  );

  const apiRegion = useMemo(() => {
    return (
      myExchanges?.find((ex) => ex.id === botDetail?.exchange_id)
        ?.user_api_settings?.[0]?.api_region ?? null
    );
  }, [myExchanges, botDetail]);

  // Load initial candle history
  useEffect(() => {
    if (!botDetail) return;

    let active = true;

    async function load() {
      setCandles([]);
      setHasMoreData(true);
      loadingRef.current = false;
      setIsLoadingMore(false);
      setLivePrice(null);
      wsReadyRef.current = false;

      currentSymbolRef.current = pairSymbol;
      currentTimeframeRef.current = selectedTimeframe;

      const data = await fetchCandles(
        pairSymbol,
        selectedTimeframe.toLowerCase(),
        null,
        100,
        botDetail?.exchange_id,
        apiRegion,
      );

      if (!active) return;

      if (data.length > 0) {
        setCandles(data);
        setLivePrice(data[data.length - 1].close);
        wsReadyRef.current = true;

        setTimeout(() => {
          const targetCandles = 50;
          const totalCandles = data.length;
          const startPercent = Math.max(
            0,
            ((totalCandles - targetCandles) / totalCandles) * 100,
          );
          setZoomState({ start: startPercent, end: 100 });
        }, 50);
      } else {
        console.warn("No candle data received");
        wsReadyRef.current = true;
      }
    }

    load();
    return () => (active = false);
  }, [pairSymbol, selectedTimeframe, botDetail]);

  // Load more historical data when scrolling left
  const loadMoreCandles = async () => {
    if (loadingRef.current || !hasMoreData || candles.length === 0) return;

    if (
      currentSymbolRef.current !== pairSymbol ||
      currentTimeframeRef.current !== selectedTimeframe
    ) {
      return;
    }

    loadingRef.current = true;
    setIsLoadingMore(true);

    const chart = chartRef.current?.getEchartsInstance();
    let savedStartIndex = null;
    let savedEndIndex = null;

    if (chart) {
      try {
        const option = chart.getOption();
        if (option.dataZoom && option.dataZoom[0]) {
          const start = option.dataZoom[0].start || 0;
          const end = option.dataZoom[0].end || 100;
          savedStartIndex = Math.floor((candles.length * start) / 100);
          savedEndIndex = Math.floor((candles.length * end) / 100);
        }
      } catch (error) {
        console.error("Error saving zoom state:", error);
      }
    }

    try {
      const oldestTime = candles[0].time * 1000;
      const oldCandleCount = candles.length;

      const getLimitByTimeframe = (tf) => {
        const tfLower = tf.toLowerCase();
        if (tfLower === "15m") return 200;
        if (tfLower === "1h") return 300;
        if (tfLower === "4h") return 300;
        if (tfLower === "1d") return 500;
        if (tfLower === "1w") return 500;
        return 300;
      };

      const fetchLimit = getLimitByTimeframe(selectedTimeframe);

      const moreData = await fetchCandles(
        pairSymbol,
        selectedTimeframe.toLowerCase(),
        oldestTime,
        fetchLimit,
        botDetail?.exchange_id,
        apiRegion,
      );

      if (
        currentSymbolRef.current !== pairSymbol ||
        currentTimeframeRef.current !== selectedTimeframe
      ) {
        loadingRef.current = false;
        setIsLoadingMore(false);
        return;
      }

      if (moreData.length === 0) {
        setHasMoreData(false);
        loadingRef.current = false;
        setIsLoadingMore(false);
        return;
      }

      const existingTimes = new Set(candles.map((c) => c.time));
      const oldestExistingTime = candles[0].time;

      const getMaxGapSeconds = (tf) => {
        const tfLower = tf.toLowerCase();
        if (tfLower === "15m") return 15 * 60 * 250;
        if (tfLower === "1h") return 60 * 60 * 400;
        if (tfLower === "4h") return 4 * 60 * 60 * 400;
        if (tfLower === "1d") return 24 * 60 * 60 * 600;
        if (tfLower === "1w") return 7 * 24 * 60 * 60 * 600;
        return 60 * 60 * 500;
      };

      const maxGap = getMaxGapSeconds(selectedTimeframe);
      const minAllowedTime = oldestExistingTime - maxGap;

      const newCandles = moreData.filter(
        (c) => !existingTimes.has(c.time) && c.time >= minAllowedTime,
      );

      if (newCandles.length === 0) {
        setHasMoreData(false);
        loadingRef.current = false;
        setIsLoadingMore(false);
        return;
      }

      if (newCandles.length < 20) {
        setHasMoreData(false);
      }

      const actualNewCount = newCandles.length;

      setCandles((prev) => {
        const combined = [...newCandles, ...prev];
        return combined.sort((a, b) => a.time - b.time);
      });

      setTimeout(() => {
        const chart = chartRef.current?.getEchartsInstance();
        if (
          chart &&
          savedStartIndex !== null &&
          savedEndIndex !== null &&
          actualNewCount > 0
        ) {
          try {
            const newTotal = oldCandleCount + actualNewCount;
            const newStartIndex = savedStartIndex + actualNewCount;
            const newEndIndex = savedEndIndex + actualNewCount;

            const newStartPercent = Math.max(
              0,
              (newStartIndex / newTotal) * 100,
            );
            const newEndPercent = Math.min(100, (newEndIndex / newTotal) * 100);

            chart.setOption(
              { dataZoom: [{ start: newStartPercent, end: newEndPercent }] },
              { lazyUpdate: true },
            );

            setZoomState({ start: newStartPercent, end: newEndPercent });
          } catch (error) {
            console.error("Error restoring zoom:", error);
          }
        }

        loadingRef.current = false;
        setIsLoadingMore(false);
      }, 150);
    } catch (error) {
      console.error("Error loading more candles:", error);
      loadingRef.current = false;
      setIsLoadingMore(false);
    }
  };

  // Build markers
  const orderMarkers = useMemo(() => {
    if (!candles.length) return [];
    return buildOrderMarkers(candles, activityData?.markers || []);
  }, [candles, activityData]);

  // Extract grid lines
  const gridLines = useMemo(() => {
    if (!activityData?.grid_lines || !Array.isArray(activityData.grid_lines)) {
      return [];
    }
    return activityData.grid_lines
      .map((line) => ({
        ...line,
        price: Number(line.price),
        label: line.label || "",
      }))
      .filter((line) => !isNaN(line.price));
  }, [activityData]);

  const calculateBarWidth = () => {
    const visibleRange = zoomState.end - zoomState.start;
    const visibleCandles = Math.floor((candles.length * visibleRange) / 100);
    if (visibleCandles > 200) return "40%";
    if (visibleCandles > 100) return "50%";
    if (visibleCandles > 50) return "60%";
    if (visibleCandles > 30) return "70%";
    return "80%";
  };

  // WebSocket — only updates livePrice, never touches candles
  useEffect(() => {
    if (!pairSymbol || !botDetail) return;

    if (wsRef.current) wsRef.current.close();

    const pairCoin = botDetail?.coin_name.split("/");
    const targetCoin = pairCoin[0];
    const baseCurrency = pairCoin[1];

    // const wsUrl = `wss://stg.xenorize.com/ws/klines/?symbol=${pairSymbol}&interval=${interval}`;
    // const wsUrl = `ws://localhost:8000/ws/klines/?target=${targetCoin}&base=${baseCurrency}&interval=1m&exchange=${botDetail?.exchange_id}`;
    let wsUrl = `wss://stg.xenorize.com/ws/klines/?target=${targetCoin}&base=${baseCurrency}&interval=1m&exchange=${botDetail?.exchange_id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    // Get api_region from myExchanges matching botDetail exchange_id
    // const matchedExchange = myExchanges?.find(
    //   (ex) => ex.id === botDetail?.exchange_id,
    // );
    // const apiRegion = matchedExchange?.user_api_settings?.[0]?.api_region;

    if (apiRegion) {
      wsUrl += `&region=${apiRegion}`;
    }

    ws.onopen = () => console.log("WebSocket connected");

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data.k) return;
        const k = data.k;
        const liveClose = +k.c;
        const liveHigh = +k.h;
        const liveLow = +k.l;
        if (isNaN(liveClose)) return;

        if (!wsReadyRef.current) return;

        setLivePrice(liveClose);

        setCandles((prev) => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = {
            ...last,
            close: liveClose,
            high: Math.max(last.high, liveHigh),
            low: Math.min(last.low, liveLow),
          };
          return updated;
        });
      } catch (err) {
        console.error("WebSocket message error:", err);
      }
    };

    ws.onerror = (error) => console.error("WebSocket error:", error);
    ws.onclose = () => console.log("WebSocket disconnected");

    return () => ws.close();
  }, [pairSymbol, selectedTimeframe, botDetail, apiRegion]);

  function getOption() {
    if (!candles || candles.length === 0) {
      return {
        backgroundColor: "#FFFFFF",
        grid: {
          left: "1%",
          right: "3%",
          top: "5%",
          bottom: 0,
          containLabel: false,
        },
        xAxis: { type: "category", data: [] },
        yAxis: { type: "value" },
        series: [],
      };
    }

    const precision = calculatePrecision(candles);
    const lastCandle = candles[candles.length - 1];

    const higherTimeframes = ["4h", "1d", "1w"];
    const isHigherTF = higherTimeframes.includes(
      selectedTimeframe.toLowerCase(),
    );
    const displayPrice =
      livePrice != null
        ? isHigherTF
          ? Math.min(lastCandle.high, Math.max(lastCandle.low, livePrice))
          : livePrice
        : null;

    const priceIsUp = lastCandle.close >= lastCandle.open;

    return {
      backgroundColor: "#FFFFFF",
      animation: false,

      // ─── KEY FIX: bottom: 0, containLabel: false ───────────────────────
      // The xAxis label strip is a separate React div below the canvas.
      // ECharts grid goes all the way to the bottom edge — no room for
      // in-canvas labels, so candlesticks can never reach them.
      grid: {
        left: "1%",
        right: "3%",
        top: "5%",
        bottom: 0,
        containLabel: false,
      },

      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: function (params) {
          if (!params || params.length === 0) return "";

          const hasMarkerHover = params.some(
            (p) =>
              p.seriesType === "scatter" && p.componentSubType === "scatter",
          );
          if (hasMarkerHover) return "";

          const candleParam = params.find(
            (p) => p.seriesType === "candlestick",
          );
          if (!candleParam || candleParam.dataIndex === undefined) return "";

          const candle = candles[candleParam.dataIndex];
          if (!candle) return "";

          return `
            <div style="padding:4px;">
              <div style="font-weight:bold;">${formatDateTime(candle.time)}</div>
              <div>O: ${candle.open.toFixed(precision)}</div>
              <div>H: ${candle.high.toFixed(precision)}</div>
              <div>L: ${candle.low.toFixed(precision)}</div>
              <div>C: ${candle.close.toFixed(precision)}</div>
            </div>
          `;
        },
      },

      xAxis: {
        type: "category",
        data: candles.map((c) => fmt(c.time)),
        boundaryGap: true,
        axisLine: { lineStyle: { color: "#E0E3EB" }, onZero: false },
        axisTick: { show: false },
        // ─── Labels hidden — drawn by <XAxisOverlay> instead ───────────
        axisLabel: { show: false },
        splitLine: { show: false },
      },

      yAxis: {
        type: "value",
        scale: true,
        position: "right",
        splitLine: { show: false },
        min: (value) => {
          const range = value.max - value.min;
          const buffer = range < 0.00001 ? value.min * 0.01 : range * 0.2;
          return value.min - buffer;
        },
        max: (value) => {
          const range = value.max - value.min;
          const buffer = range < 0.00001 ? value.max * 0.01 : range * 0.2;
          return value.max + buffer;
        },
        axisLabel: {
          formatter: (val) => val.toFixed(precision),
          color: "#787B86",
        },
      },

      dataZoom: [
        {
          type: "inside",
          xAxisIndex: [0],
          start: zoomState.start,
          end: zoomState.end,
          filterMode: "filter",
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          minValueSpan: 10,
        },
        {
          type: "inside",
          yAxisIndex: [0],
          filterMode: "none",
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
      ],

      series: [
        {
          type: "candlestick",
          data: candles.map((c) => [c.open, c.close, c.low, c.high]),
          itemStyle: {
            color: "#26A69A",
            color0: "#EF5350",
            borderColor: "#26A69A",
            borderColor0: "#EF5350",
            borderWidth: 1,
          },
          barWidth: calculateBarWidth(),
          barMaxWidth: "90%",
          barMinWidth: 2,
        },

        ...(candles.length > 0 && gridLines.length > 0
          ? [
              {
                type: "line",
                name: "GRID_LINES",
                silent: true,
                data: [],
                markLine: {
                  symbol: "none",
                  precision: precision,
                  clip: false,
                  data: gridLines.map((line) => ({
                    yAxis: Number(line.price),
                    label: {
                      show: true,
                      position: "end",
                      formatter: (params) => {
                        const target = gridLines.find(
                          (g) => Number(g.price) === Number(params.value),
                        );
                        return target
                          ? `${target.label}: ${params.value.toFixed(precision)}`
                          : params.value;
                      },
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      color: "#2962FF",
                      padding: [3, 5],
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: "bold",
                      borderWidth: 1,
                      borderColor: "#2962FF",
                    },
                    lineStyle: { width: 1, color: "#2962FF", type: "dashed" },
                  })),
                },
              },
            ]
          : []),

        ...(displayPrice != null && candles.length > 0
          ? [
              {
                type: "line",
                name: "LIVE PRICE",
                markLine: {
                  silent: true,
                  symbol: "none",
                  data: [{ yAxis: displayPrice }],
                  label: {
                    show: true,
                    formatter: () =>
                      displayPrice != null
                        ? displayPrice.toFixed(precision)
                        : "",
                    position: "end",
                    backgroundColor: priceIsUp ? "#26A69A" : "#EF5350",
                    color: "#fff",
                    padding: [4, 6],
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: "bold",
                  },
                  lineStyle: {
                    width: 1.2,
                    color: priceIsUp ? "#26A69A" : "#EF5350",
                    type: "solid",
                  },
                },
                data: [],
              },
            ]
          : []),

        {
          type: "scatter",
          name: "Trade Orders",
          z: 10,
          symbolSize: 24,
          data: (() => {
            if (!orderMarkers.length) return [];

            const stackMap = {};

            return orderMarkers
              .map((m) => {
                const markerTime = m.time;

                let matchIdx = candles.findIndex((c, i) => {
                  const nextCandleTime = candles[i + 1]
                    ? candles[i + 1].time
                    : Infinity;
                  return markerTime >= c.time && markerTime < nextCandleTime;
                });

                if (matchIdx === -1) {
                  matchIdx = candles.findIndex((c) => c.time === markerTime);
                }

                if (matchIdx === -1) return null;

                const candle = candles[matchIdx];
                if (!candle) return null;

                const stackKey = `${matchIdx}_${m.type}`;
                const stackCount = stackMap[stackKey] || 0;
                stackMap[stackKey] = stackCount + 1;

                const priceGap = candle.close * 0.0075;
                const safetyBuffer = candle.close * 0.02;
                const totalOffset = safetyBuffer + priceGap * stackCount * 4;

                const yPos =
                  m.type === "buy"
                    ? candle.low - totalOffset
                    : candle.high + totalOffset;

                return {
                  value: [fmt(candle.time), yPos],
                  coord: [fmt(candle.time), yPos],
                  markerType: m.type,
                  markerData: m,
                };
              })
              .filter(Boolean);
          })(),

          symbol: function (value, params) {
            if (!params?.data?.markerType) return "circle";
            const markerType = params.data.markerType;
            const imagePath =
              markerType === "buy"
                ? "/images/marker/buy_marker.png"
                : "/images/marker/sell_marker.png";
            return `image://${imagePath}`;
          },

          tooltip: {
            trigger: "item",
            formatter: (params) => {
              if (!params?.data?.markerData) return "";
              const m = params.data.markerData;
              const typeColor = m.type === "buy" ? "#26A69A" : "#EF5350";
              return `
                <div style="padding: 8px; border-left: 4px solid ${typeColor}; background: #fff;">
                  <div style="color: ${typeColor}; font-weight: bold;">${m.type.toUpperCase()} ORDER</div>
                  <div style="color: #131722;">Price: ${Number(m.price).toFixed(precision)}</div>
                  <div style="font-size: 11px; color: #787B86;">${formatDateTime(m.time)}</div>
                  ${m.label ? `<div style="font-size:12px; margin-top:4px;">${m.label}</div>` : ""}
                </div>
              `;
            },
          },
        },
      ],
    };
  }

  const timeframes = ["15M", "1H", "4H", "1D", "1W"];

  // Chart height minus the xAxis overlay strip
  const chartHeight = height - XAXIS_HEIGHT;

  return (
    <div style={{ backgroundColor: "#FFFFFF", position: "relative" }}>
      {isLoadingMore && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            background: "#2D224E",
            color: "white",
            padding: "8px 16px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1000,
          }}
        >
          Loading historical data...
        </div>
      )}

      {/* ECharts canvas — no xAxis labels inside */}
      <ReactECharts
        ref={chartRef}
        option={getOption()}
        style={{ height: chartHeight, width: "100%" }}
        opts={{ renderer: "canvas" }}
        notMerge={false}
        lazyUpdate={true}
        onEvents={{
          dataZoom: (params) => {
            if (dataZoomTimeoutRef.current) {
              clearTimeout(dataZoomTimeoutRef.current);
            }

            dataZoomTimeoutRef.current = setTimeout(() => {
              const chart = chartRef.current?.getEchartsInstance();
              if (chart) {
                try {
                  const option = chart.getOption();
                  if (option.dataZoom && option.dataZoom[0]) {
                    const newStart = option.dataZoom[0].start || 0;
                    const newEnd = option.dataZoom[0].end || 100;

                    setZoomState({ start: newStart, end: newEnd });

                    if (
                      newStart < 15 &&
                      !loadingRef.current &&
                      hasMoreData &&
                      candles.length > 0
                    ) {
                      loadMoreCandles();
                    }
                  }
                } catch (error) {
                  console.error("Error in dataZoom handler:", error);
                }
              }
            }, 100);
          },
        }}
      />

      {/* ── TradingView-style xAxis strip ──────────────────────────────────
          Rendered as a plain white div BELOW the canvas. Candlesticks are
          physically inside the canvas above, so they can never overlap here.
      ───────────────────────────────────────────────────────────────────── */}
      <XAxisOverlay
        chartRef={chartRef}
        candles={candles}
        zoomState={zoomState}
      />

      <div className="flex items-center gap-2 mt-3 pb-2">
        {timeframes.map((tf) => (
          <button
            key={tf}
            onClick={() => setSelectedTimeframe(tf)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
              tf === selectedTimeframe
                ? "bg-[#F0F3FA] text-[#131722]"
                : "bg-[transparent] text-[#131722] hover:bg-[#F0F3FA]"
            }`}
          >
            {tf}
          </button>
        ))}
      </div>
    </div>
  );
}
